import React from "react";
import { LegalLayout } from "./LegalLayout";

const LAST_UPDATED = "12 June 2026";

/**
 * Accessibility Statement — aligned with WCAG 2.1 AA and the UK Equality Act 2010.
 * PL + EN versions; DE/FR/ES users see the authoritative English text with a
 * localized notice (handled by LegalLayout, same as the other legal pages).
 */
export const AccessibilityPage = ({ t, language = "en" }) => {
  if (language === "pl") {
    return (
      <LegalLayout title="Deklaracja dostępności" lastUpdated={LAST_UPDATED} t={t} language={language}>
        <h2>1. Nasze zobowiązanie</h2>
        <p>
          <strong>Worthify</strong> (prowadzony przez Worthify Ltd) zobowiązuje się do zapewnienia dostępności swojego serwisu jak najszerszemu gronu użytkowników — niezależnie od niepełnosprawności, technologii czy umiejętności. Dążymy do zgodności z wytycznymi <strong>WCAG 2.1 na poziomie AA</strong> oraz brytyjską ustawą <strong>Equality Act 2010</strong>.
        </p>

        <h2>2. Co robimy, aby serwis był dostępny</h2>
        <ul>
          <li><strong>Nawigacja klawiaturą</strong> — wszystkie interaktywne elementy (przyciski, pola formularzy, linki) są osiągalne klawiszem Tab i obsługiwane Enterem/Spacją</li>
          <li><strong>Czytniki ekranu</strong> — używamy semantycznego HTML, etykiet <code>aria-label</code> i opisów alternatywnych dla elementów graficznych</li>
          <li><strong>Kontrast kolorów</strong> — ciemny motyw serwisu został zaprojektowany z kontrastem tekstu spełniającym wymogi WCAG AA (min. 4,5:1 dla tekstu standardowego)</li>
          <li><strong>Skalowanie tekstu</strong> — układ strony pozostaje czytelny przy powiększeniu do 200%</li>
          <li><strong>Responsywność mobilna</strong> — serwis działa na ekranach dotykowych z odpowiednio dużymi obszarami dotyku (min. 44×44 px)</li>
          <li><strong>Prosty język</strong> — raporty MOT tłumaczymy z żargonu technicznego na zrozumiałe wyjaśnienia w 5 językach (EN/PL/DE/FR/ES)</li>
          <li><strong>Animacje</strong> — animacje interfejsu są subtelne i nie zawierają migających treści mogących wywołać napady padaczki fotogennej (zgodnie z WCAG 2.3.1)</li>
        </ul>

        <h2>3. Asystent głosowy AI — dostępność bez barier</h2>
        <p>
          Funkcja <strong>„Asystent AI dzwoni za Ciebie"</strong> została zaprojektowana m.in. z myślą o osobach, dla których rozmowa telefoniczna stanowi barierę — w tym osobach niesłyszących, niedosłyszących, z zaburzeniami mowy, lękiem telefonicznym lub barierą językową. Asystent wykonuje połączenie do sprzedawcy w Twoim imieniu, negocjuje cenę i dostarcza pełny pisemny transkrypt rozmowy oraz podsumowanie — bez konieczności wykonywania rozmowy samodzielnie.
        </p>

        <h2>4. Znane ograniczenia</h2>
        <ul>
          <li>Raporty PDF generowane przez serwis mogą nie być w pełni otagowane pod kątem czytników ekranu — pracujemy nad poprawą</li>
          <li>Niektóre wykresy historii MOT przekazują informacje wizualnie; kluczowe dane są jednak zawsze dostępne także w formie tekstowej</li>
          <li>Strona płatności Stripe Checkout jest zarządzana przez Stripe — jej dostępność opisuje <a href="https://stripe.com/gb/legal/website-accessibility-policy" target="_blank" rel="noopener noreferrer">polityka dostępności Stripe</a></li>
        </ul>

        <h2>5. Zgłaszanie problemów z dostępnością</h2>
        <p>
          Jeśli napotkasz problem z dostępnością lub potrzebujesz treści w innym formacie, napisz do nas: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>. Odpowiadamy w ciągu <strong>5 dni roboczych</strong> i traktujemy każde zgłoszenie priorytetowo.
        </p>

        <h2>6. Procedura egzekwowania</h2>
        <p>
          Jeśli nie jesteś zadowolony z naszej odpowiedzi na zgłoszenie, możesz skontaktować się z <a href="https://www.equalityadvisoryservice.com/" target="_blank" rel="noopener noreferrer">Equality Advisory and Support Service (EASS)</a> — brytyjskim organem wspierającym egzekwowanie Equality Act 2010.
        </p>

        <h2>7. Przegląd deklaracji</h2>
        <p>
          Deklaracja jest przeglądana przy każdej istotnej zmianie serwisu. Ostatnia aktualizacja: <strong>{LAST_UPDATED}</strong>.
        </p>
      </LegalLayout>
    );
  }

  // English (authoritative)
  return (
    <LegalLayout title="Accessibility Statement" lastUpdated={LAST_UPDATED} t={t} language={language}>
      <h2>1. Our commitment</h2>
      <p>
        <strong>Worthify</strong> (operated by Worthify Ltd) is committed to making its service accessible to the widest possible audience, regardless of disability, technology or ability. We aim to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 level AA</strong> and the UK <strong>Equality Act 2010</strong>.
      </p>

      <h2>2. What we do to keep the service accessible</h2>
      <ul>
        <li><strong>Keyboard navigation</strong> — all interactive elements (buttons, form fields, links) are reachable with Tab and operable with Enter/Space</li>
        <li><strong>Screen readers</strong> — we use semantic HTML, <code>aria-label</code> attributes and alternative descriptions for graphical elements</li>
        <li><strong>Colour contrast</strong> — the dark theme is designed with text contrast meeting WCAG AA requirements (min. 4.5:1 for standard text)</li>
        <li><strong>Text scaling</strong> — the layout remains readable when zoomed to 200%</li>
        <li><strong>Mobile responsiveness</strong> — the service works on touch screens with adequately sized touch targets (min. 44×44 px)</li>
        <li><strong>Plain language</strong> — we translate MOT technical jargon into clear explanations in 5 languages (EN/PL/DE/FR/ES)</li>
        <li><strong>Animations</strong> — interface animations are subtle and contain no flashing content that could trigger photosensitive seizures (per WCAG 2.3.1)</li>
      </ul>

      <h2>3. AI voice assistant — accessibility without barriers</h2>
      <p>
        The <strong>"AI Call Assistant calls for you"</strong> feature was designed in part for people for whom a phone call is a barrier — including deaf and hard-of-hearing users, people with speech impairments, phone anxiety, or a language barrier. The assistant places the call to the seller on your behalf, negotiates the price, and delivers a full written transcript and summary — no need to make the call yourself.
      </p>

      <h2>4. Known limitations</h2>
      <ul>
        <li>PDF reports generated by the service may not be fully tagged for screen readers — we are working to improve this</li>
        <li>Some MOT history charts convey information visually; however, key data is always also available in text form</li>
        <li>The Stripe Checkout payment page is managed by Stripe — its accessibility is covered by <a href="https://stripe.com/gb/legal/website-accessibility-policy" target="_blank" rel="noopener noreferrer">Stripe's accessibility policy</a></li>
      </ul>

      <h2>5. Reporting accessibility problems</h2>
      <p>
        If you encounter an accessibility problem or need content in a different format, contact us at <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>. We respond within <strong>5 working days</strong> and treat every report as a priority.
      </p>

      <h2>6. Enforcement procedure</h2>
      <p>
        If you are not happy with our response, you can contact the <a href="https://www.equalityadvisoryservice.com/" target="_blank" rel="noopener noreferrer">Equality Advisory and Support Service (EASS)</a> — the UK body that supports enforcement of the Equality Act 2010.
      </p>

      <h2>7. Review of this statement</h2>
      <p>
        This statement is reviewed whenever the service changes materially. Last updated: <strong>{LAST_UPDATED}</strong>.
      </p>
    </LegalLayout>
  );
};